/****************************************************************************************************
 * V2 PAGE TEMPLATE
 * -------------------------------------------------------------------------------------------------
 * The vi2Page template is used to render pages that are created as part of the VI2 project which
 * is initially a partnership B2B project. This template is used to render the pages that are
 * created in the VI2 project. Depending on the outcomes of the B2B project and other factors, this
 * template may be used to render other pages in the future.
 *
 * -------------------------------------------------------------------------------------------------
 * * Rules of V2
 * - The V2 project uses the Sanity CMS to manage the content.
 * - The V2 project uses the MUI (Material-UI) component library to manage the UI.
 * - The V2 project implements best practices for development that should be strictly followed
 * -- 100% TypeScript Safety
 * -- Centralized and reusable components that may be customized
 * -- Components planned and executed for maximum reusability
 * -- Components that become a part of a design system and library
 * -- No Shortcuts for speed or convenience
 * -- No hardcoding of values
 * -- Centralized GraphQL Fragments
 * -- Limited Prop Drilling
 * -- Use of component level state context
 * *************************************************************************************************/

import React, { useEffect } from 'react';
import { graphql } from 'gatsby';
import { Brand } from 'src/components';
import { useDevMode } from 'src/hooks';

// This is V2 Store
import { useStoreV2 } from 'src/store/StoreV2';
import { Section } from 'src/components';
import { title } from 'process';

interface SanityPartnerProps {
  data: {
    sanityPage: SanityPage;
  };
}

// const PartnerPage = (props: PageProps<SanityPartnerProps>) => {
const PartnerPage = (props: SanityPartnerProps) => {
  const { data } = props;
  const devMode = useDevMode();

  const theme = data?.sanityPage?.brand;

  // brand specific layout data into the store
  // The reason is to avoid prop drilling
  const { updateState, layout } = useStoreV2();

  useEffect(() => {
    updateState((state:any) => {      
      state.layout = theme?.layout ?? {};
    });
  }, [theme]);

  return (
    <>
      {/* <App {...props}> */}

      {/********************************************************************************
       * BRAND
       * -------------------------------------------------------------------------------
       * The page is wrapped in a Brand component which provides the theme for the page.
       * TODO: Consider moving the Brand component to the layout component.
       * *****************************************************************************/}
      <Brand theme={theme}>
        {/* <PartnerTopNav />         */}
        <Section sections={data.sanityPage.sections} />        
      </Brand>      
    </>
  );
};

/****************************************************************************************************
 * PAGE SEO
 * -------------------------------------------------------------------------------------------------
 * The Head component is used to manage the SEO for the page. It is used to set the title, meta
 * description, and open graph data for the page.
 * **************************************************************************************************/
export function Head(props: SanityPartnerProps) {
  
  const { data } = props;
  if (!data) return null;
  const { seo = null } = data.sanityPage;

  return (
    <>
      <title>{seo?.title ?? 'Elo Smart Nutrition'}</title>
      <meta name="description" content={seo?.metaDescription ?? 'Elo Smart Nutrition'} />
      <meta property="og:title" content={seo?.ogTitle ?? 'Elo Smart Nutrition'} />
      <meta property="og:description" content={seo?.ogDescription ?? 'Elo Smart Nutrition'} />
      <meta property="og:image" content={seo?.ogImage?.asset?.url ?? ''} />
      <meta property="og:url" content={seo?.ogURL ?? 'https://elo.health'} />
      <meta property="og:type" content={'website'} />
      <meta name="robots" content={seo?.disableIndexing ? 'noindex' : 'index'} />
    </>
  );
}


/****************************************************************************************************
 * PAGE QUERY
 * -------------------------------------------------------------------------------------------------
 * Gatsby will use the page query to get the data it needs to render the page. The slug variable
 * is passed in the context when Gatsby creates the page.
 *
 * -------------------------------------------------------------------------------------------------
 * * Fragments
 * Please centralize your fragments in the src/fragments directory. This will help you and other
 * developers to find, debug and reuse them.
 *
 * -------------------------------------------------------------------------------------------------
 * * sanityPage Fragment
 * All fragments are located in the src/fragments directory.
 * Find the page fragment at: src/fragments/sanityFragments/sanityPage.Fragment.ts
 *
 * **************************************************************************************************/
export const pageQuery = graphql`
  query Vi2Page($slug: String!) {
    sanityPage(slug: { current: { eq: $slug } }) {
      ...sanityPage
    }
  }
`;

export default PartnerPage;